<template>
  <b-modal
    v-model="isActive"
    has-modal-card
    class="data-completion"
    :can-cancel="['escape', 'x']"
  >
    <form @submit.prevent="applyFilters" class="enable-select-text">
      <div class="modal-card data-completion-card">
        <header class="modal-card-head">
          <p class="modal-card-title title-success">Filters</p>
        </header>
        <section class="modal-card-body">
          <div v-for="(stream, streamName) in streamFilters" :key="streamName">
            <div class="stream-header">
              <h4>
                <strong>{{ streamName }}</strong>
              </h4>
              <b-button
                type="is-primary"
                size="is-small"
                @click="addFilter(streamName)"
              >
                Add Filter
              </b-button>
            </div>

            <div class="stream-filters">
              <div
                v-for="(filter, index) in stream.filters"
                :key="`${streamName}-${index}`"
              >
                <div class="columns">
                  <b-field class="column is-4">
                    <b-select
                      expanded
                      v-model="filter.field"
                      placeholder="Please Select a Field"
                      required
                      @input="onFieldChange($event, streamName, index)"
                    >
                      <option
                        v-for="(column, idx) in getAvailableColumns(streamName)"
                        :value="column.field"
                        :key="idx"
                      >
                        {{ column.label }}
                      </option>
                    </b-select>
                  </b-field>

                  <b-field class="column is-5" v-if="filter.field">
                    <!-- Date Range Field -->
                    <div
                      v-if="
                        getFieldType(filter.field, streamName) === 'dateField'
                      "
                      class="date-range-container"
                    >
                      <b-field label="From">
                        <b-datepicker
                          v-model="filter.valueFrom"
                          placeholder="From date"
                          icon="calendar-today"
                          trap-focus
                        ></b-datepicker>
                      </b-field>
                      <b-field label="To">
                        <b-datepicker
                          v-model="filter.valueTo"
                          placeholder="To date"
                          icon="calendar-today"
                          trap-focus
                        ></b-datepicker>
                      </b-field>
                    </div>

                    <!-- Number Range Field -->
                    <b-field
                      v-else-if="
                        getFieldType(filter.field, streamName) === 'numberRange'
                      "
                    >
                      <NumberRangePicker
                        :id="`${streamName}-${index}-${filter.field}`"
                        @assignNumberRangeValue="
                          onNumberRangeChange($event, streamName, index)
                        "
                        :clearFilter="false"
                      />
                    </b-field>

                    <!-- Dropdown Field -->
                    <b-field
                      v-else-if="
                        getFieldType(filter.field, streamName) ===
                          'multiSelectFilter'
                      "
                    >
                      <b-select
                        expanded
                        v-model="filter.value"
                        placeholder="Please select a value"
                        required
                      >
                        <option
                          v-for="(option, idx) in getFieldOptions(
                            filter.field,
                            streamName
                          )"
                          :value="option.name"
                          :key="idx"
                        >
                          {{ option.name }}
                        </option>
                      </b-select>
                    </b-field>

                    <!-- Text/Input Field -->
                    <b-field v-else>
                      <b-input
                        required
                        v-model="filter.value"
                        placeholder="Enter value"
                      ></b-input>
                    </b-field>
                  </b-field>

                  <div class="column is-2 delete-stream-button">
                    <a
                      class="delete-field"
                      @click="deleteFilter(streamName, index)"
                    >
                      <b-icon icon="delete" type="is-danger"></b-icon>
                    </a>
                  </div>
                </div>
              </div>

              <div v-if="stream.filters.length === 0" class="no-filters">
                <em>No filters applied for this sheet</em>
              </div>
            </div>
            <hr />
          </div>
        </section>
        <footer class="modal-card-foot">
          <b-button
            label="Clear All"
            class="is-normal"
            @click="clearAllFilters"
            type="is-danger"
          />
          <b-button label="Cancel" class="is-normal" @click="cancel" />
          <b-button slot="right" type="is-primary" native-type="submit">
            Apply Filters
          </b-button>
        </footer>
      </div>
    </form>
  </b-modal>
</template>

<script>
import * as dayjs from "dayjs";
import NumberRangePicker from "../NumberRangePicker.vue";

export default {
  name: "ProductMappingFilters",
  components: { NumberRangePicker },
  props: {
    isActive: {
      type: Boolean,
      default: false
    },
    columns: {
      type: Array,
      default: () => []
    },
    currentFilters: {
      type: Object,
      default: () => ({})
    },
    allStreamColumns: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      streamFilters: {
        "PRODUCT SHEET": { filters: [] },
        BATT: { filters: [] },
        PACK: { filters: [] },
        WEEE: { filters: [] },
        "BUNDLE INFORMATION": { filters: [] }
      }
    };
  },
  methods: {
    addFilter(streamName) {
      this.streamFilters[streamName].filters.push({
        field: "",
        value: "",
        valueFrom: null,
        valueTo: null
      });
    },
    deleteFilter(streamName, index) {
      this.streamFilters[streamName].filters.splice(index, 1);
    },
    onFieldChange(fieldName, streamName, index) {
      // Reset value when field changes
      this.streamFilters[streamName].filters[index].value = "";
      this.streamFilters[streamName].filters[index].valueFrom = null;
      this.streamFilters[streamName].filters[index].valueTo = null;
    },
    getAvailableColumns(streamName) {
      // If we have stream-specific columns from parent, use those
      if (
        this.allStreamColumns &&
        this.allStreamColumns[streamName] &&
        this.allStreamColumns[streamName].length > 0
      ) {
        return this.allStreamColumns[streamName].filter(
          col =>
            col.field !== "actions" &&
            col.searchable &&
            col.label &&
            col.label !== ""
        );
      }
      // Fallback to current columns
      return this.columns.filter(
        col =>
          col.field !== "actions" &&
          col.searchable &&
          col.label &&
          col.label !== ""
      );
    },
    getFieldType(fieldName, streamName) {
      const columns = this.getAvailableColumns(streamName);
      const column = columns.find(col => col.field === fieldName);
      return column ? column.type : "input";
    },
    getFieldOptions(fieldName, streamName) {
      const columns = this.getAvailableColumns(streamName);
      const column = columns.find(col => col.field === fieldName);
      return column && column.allStatusVals ? column.allStatusVals : [];
    },
    onNumberRangeChange(rangeValue, streamName, index) {
      // rangeValue is the formData object with option1 and option2
      // streamName is passed as the second parameter (id in the original signature)
      this.streamFilters[streamName].filters[index].valueFrom =
        rangeValue.option1;
      this.streamFilters[streamName].filters[index].valueTo =
        rangeValue.option2;
    },
    applyFilters() {
      // Build query object with filters for each stream
      const allStreamQueries = {};
      const allStreamFilterData = {};

      Object.keys(this.streamFilters).forEach(streamName => {
        const queryParams = [];
        const filters = this.streamFilters[streamName].filters;
        const filterData = [];

        filters.forEach(filter => {
          if (!filter.field) return;

          const fieldType = this.getFieldType(filter.field, streamName);

          // Handle date range
          if (fieldType === "dateField") {
            if (filter.valueFrom || filter.valueTo) {
              filterData.push({
                field: filter.field,
                fieldType: "dateField",
                valueFrom: filter.valueFrom
                  ? dayjs(filter.valueFrom).format("YYYY-MM-DD")
                  : null,
                valueTo: filter.valueTo
                  ? dayjs(filter.valueTo).format("YYYY-MM-DD")
                  : null
              });

              if (filter.valueFrom) {
                const fromDate = dayjs(filter.valueFrom).format("YYYY-MM-DD");
                queryParams.push(
                  `${filter.field}=${encodeURIComponent(fromDate)}`
                );
              }
              if (filter.valueTo) {
                const toDate = dayjs(filter.valueTo).format("YYYY-MM-DD");
                queryParams.push(
                  `${filter.field}=${encodeURIComponent(toDate)}`
                );
              }
            }
          }
          // Handle number range
          else if (fieldType === "numberRange") {
            const hasFrom =
              filter.valueFrom !== null &&
              filter.valueFrom !== "" &&
              filter.valueFrom !== undefined;
            const hasTo =
              filter.valueTo !== null &&
              filter.valueTo !== "" &&
              filter.valueTo !== undefined;

            if (hasFrom || hasTo) {
              filterData.push({
                field: filter.field,
                fieldType: "numberRange",
                valueFrom: hasFrom ? filter.valueFrom : null,
                valueTo: hasTo ? filter.valueTo : null
              });

              if (hasFrom) {
                queryParams.push(
                  `${filter.field}=${encodeURIComponent(filter.valueFrom)}`
                );
              }
              if (hasTo) {
                queryParams.push(
                  `${filter.field}=${encodeURIComponent(filter.valueTo)}`
                );
              }
            }
          }
          // Handle regular fields
          else if (filter.value !== "" && filter.value !== null) {
            filterData.push({
              field: filter.field,
              fieldType: fieldType,
              value: filter.value
            });
            queryParams.push(
              `${filter.field}=${encodeURIComponent(filter.value)}`
            );
          }
        });

        allStreamQueries[streamName] =
          queryParams.length > 0 ? `&${queryParams.join("&")}&` : "&";
        allStreamFilterData[streamName] = filterData;
      });

      this.$emit("applyFilters", allStreamQueries, allStreamFilterData);
      this.$emit("cancel");
    },
    clearAllFilters() {
      Object.keys(this.streamFilters).forEach(streamName => {
        this.streamFilters[streamName].filters = [];
      });

      const emptyQueries = {};
      const emptyFilterData = {};
      Object.keys(this.streamFilters).forEach(streamName => {
        emptyQueries[streamName] = "&";
        emptyFilterData[streamName] = [];
      });

      this.$emit("applyFilters", emptyQueries, emptyFilterData);
      this.$emit("cancel");
    },
    cancel() {
      this.$emit("cancel");
    },
    populateFiltersFromQuery() {
      // Parse current filters and populate the modal for all streams
      Object.keys(this.streamFilters).forEach(streamName => {
        this.streamFilters[streamName].filters = [];
      });

      if (
        !this.currentFilters ||
        Object.keys(this.currentFilters).length === 0
      ) {
        return;
      }

      // Iterate through each stream's filters
      Object.keys(this.streamFilters).forEach(streamName => {
        const streamFilterContainer = this.currentFilters[streamName];

        // Extract filters array from the new structure {filters: [...], query: "..."}
        const streamFilterData =
          streamFilterContainer?.filters || streamFilterContainer;

        if (
          !streamFilterData ||
          !Array.isArray(streamFilterData) ||
          streamFilterData.length === 0
        ) {
          return;
        }

        const newFilters = [];

        streamFilterData.forEach(filterItem => {
          if (filterItem && filterItem.field) {
            // Handle date range
            if (filterItem.fieldType === "dateField") {
              newFilters.push({
                field: filterItem.field,
                value: "",
                valueFrom: filterItem.valueFrom
                  ? new Date(filterItem.valueFrom)
                  : null,
                valueTo: filterItem.valueTo
                  ? new Date(filterItem.valueTo)
                  : null
              });
            }
            // Handle number range
            else if (filterItem.fieldType === "numberRange") {
              newFilters.push({
                field: filterItem.field,
                value: "",
                valueFrom:
                  filterItem.valueFrom !== undefined
                    ? filterItem.valueFrom
                    : null,
                valueTo:
                  filterItem.valueTo !== undefined ? filterItem.valueTo : null
              });
            }
            // Handle regular fields
            else if (filterItem.value !== undefined) {
              newFilters.push({
                field: filterItem.field,
                value: filterItem.value,
                valueFrom: null,
                valueTo: null
              });
            }
          }
        });

        if (newFilters.length > 0) {
          this.streamFilters[streamName].filters = newFilters;
        }
      });
    }
  },
  watch: {
    isActive(newValue) {
      if (newValue) {
        // When modal opens, populate from current filters
        this.populateFiltersFromQuery();
      }
    }
  }
};
</script>

<style scoped>
.data-completion-card {
  width: 800px;
  max-height: 80vh;
}

.modal-card-body {
  max-height: 60vh;
  overflow-y: auto;
}

.stream-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.stream-header h4 {
  margin: 0;
}

.stream-filters {
  padding-left: 15px;
  padding-right: 15px;
  margin-bottom: 20px;
}

.no-filters {
  padding: 10px 0;
  color: #999;
}

.delete-stream-button {
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-field {
  cursor: pointer;
}

hr {
  margin: 20px 0;
}

.date-range-container {
  display: flex;
  gap: 10px;
  width: 100%;
}

.date-range-container .field {
  flex: 1;
}
</style>
